import React, { useEffect, useState } from 'react';
import {
    ActionIcon,
    Badge,
    Button,
    Flex,
    Highlight,
    Paper,
    ScrollArea,
    Stack,
    Table,
    TextInput,
    Title,
} from '@mantine/core';
import { IconArrowRight, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { useAlertRules } from '../../../global/entities/Alert/queries';
import { IAlertRule } from '../../../global/entities/Alert/Alert.ts';
import tableTools from '../../../global/helpers/tableTools.ts';
const AlertRuleTable: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { data, isLoading } = useAlertRules();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [sortedRules, setSortedRules] = useState<IAlertRule[]>();
    const { sortBy, reverseSortDirection, setReverseSortDirection, setSortBy } = useTable();

    const isEn = i18n.language === 'en';

    const setSorting = (field: string, data: any[]) => {
        const reversed = field === sortBy ? !reverseSortDirection : false;
        setReverseSortDirection(reversed);
        setSortBy(field);
        setSortedRules(tableTools.sortData(data, { sortBy: field, reversed, search }));
    };

    useEffect(() => {
        if (!data) return;
        setSortedRules(data);
    }, [data]);

    const onClickRule = (id: number) => {
        navigate(`/adminDashboard/alert-rules/${id}`);
    };

    if (isLoading) return <Loading visible={true} loadingMessage={t('global.loading')} />;

    return (
        <>
            <Flex mb={'xl'} mt={'xl'} align={'center'} justify={'space-between'}>
                <Button onClick={() => navigate('/adminDashboard/alert-rules')} variant="transparent">
                    <Title c="black" order={4}>
                        {t('alertRule.title')}
                    </Title>
                </Button>
            </Flex>
            <Paper w={'100%'}>
                <Stack gap={'md'} h={'100%'}>
                    <TextInput
                        placeholder={t('global.search')}
                        rightSection={<IconSearch />}
                        value={search}
                        color={'var(--mantine-color-default-color)'}
                        width={'100%'}
                        radius={'md'}
                        onChange={(el) => setSearch(el.currentTarget.value)}
                    />
                    <ScrollArea w="100%" pb={'md'}>
                        <Table verticalSpacing="xs">
                            <Table.Thead>
                                {sortedRules && (
                                    <Table.Tr>
                                        <Th
                                            sorted={sortBy === 'nameEn'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('nameEn', sortedRules)}
                                        >
                                            {t('alertRule.table.name')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'sourceType'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('sourceType', sortedRules)}
                                        >
                                            {t('alertRule.table.sourceType')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'evaluationMode'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('evaluationMode', sortedRules)}
                                        >
                                            {t('alertRule.table.evaluationMode')}
                                        </Th>
                                        <Th
                                            sorted={sortBy === 'isEnabled'}
                                            reversed={reverseSortDirection}
                                            onSort={() => setSorting('isEnabled', sortedRules)}
                                        >
                                            {t('alertRule.table.status')}
                                        </Th>
                                        <Table.Th />
                                    </Table.Tr>
                                )}
                            </Table.Thead>
                            <Table.Tbody>
                                {sortedRules &&
                                    sortedRules.map((rule: IAlertRule, index: number) => {
                                        const name = isEn ? rule.nameEn : rule.nameFr;
                                        const description = isEn ? rule.descriptionEn : rule.descriptionFr;
                                        const isSearch =
                                            containsStringIgnoreCase(
                                                [name, rule.sourceType, rule.evaluationMode, rule.code],
                                                search,
                                            ) || !search;
                                        return (
                                            <Table.Tr
                                                key={rule.id}
                                                hidden={!isSearch}
                                                style={{
                                                    background:
                                                        index % 2 === 1
                                                            ? undefined
                                                            : 'var(--Background-Gray-light, #E9ECEF)',
                                                }}
                                            >
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    >
                                                        {name}
                                                    </Highlight>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-6)',
                                                            fontWeight: 500,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 12, color: 'var(--mantine-color-gray-6)' } }}
                                                    >
                                                        {description}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    >
                                                        {rule.sourceType}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    >
                                                        {rule.evaluationMode}
                                                    </Highlight>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Badge color={rule.isEnabled ? 'green' : 'gray'} variant="light">
                                                        {rule.isEnabled ? t('alertRule.enabled') : t('alertRule.disabled')}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <ActionIcon variant="transparent" onClick={() => onClickRule(rule.id)} aria-label={t('alertRule.viewDetails')}>
                                                        <IconArrowRight color={'var(--mantine-color-blue-6)'} />
                                                    </ActionIcon>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Stack>
            </Paper>
        </>
    );
};

const WrappedAlertRuleTable: React.FC = () => {
    return (
        <TableContextProvider>
            <AlertRuleTable />
        </TableContextProvider>
    );
};

export default WrappedAlertRuleTable;
